export enum AssignmentType {
    Drive = 1,
    Delivery = 2,
}

export class AssignmentsData {
    private _assignments: Assignment[] = [];
    private _totalTime: number = 0;
    private _totalDriveTime: number = 0;
    private _totalDeliveryTime: number = 0;
    private _totalPackages: number = 0;
    private _totalStops: number = 0;
    private _totalDistance: number = 0;
    private _totalCost: number = 0;

    public addAssignment(assignment: Assignment) {
        if (assignment.totalTime < 0) {
            throw new Error('Assignment total time cannot be negative');
        }
        if (assignment.totalDriveTime < 0) {
            throw new Error('Assignment total drive time cannot be negative');
        }
        if (assignment.totalDeliveryTime < 0) {
            throw new Error('Assignment total delivery time cannot be negative');
        }
        if (assignment.packages < 0) {
            throw new Error('Assignment packages cannot be negative');
        }
        if (assignment.stops < 0) {
            throw new Error('Assignment stops cannot be negative');
        }
        if (assignment.totalDistance < 0) {
            throw new Error('Assignment total distance cannot be negative');
        }
        if (assignment.cost < 0) {
            throw new Error('Assignment cost cannot be negative');
        }
        this._totalTime += assignment.totalTime;
        this._totalDriveTime += assignment.totalDriveTime;
        this._totalDeliveryTime += assignment.totalDeliveryTime;
        this._totalPackages += assignment.packages;
        this._totalStops += assignment.stops;
        this._totalDistance += assignment.totalDistance;
        this._totalCost += assignment.cost;
        this._assignments.push(assignment);
    }

    get assignments(): Assignment[] {
        return this._assignments;
    }

    get totalTime(): number {
        return this._totalTime;
    }

    get totalDriveTime(): number {
        return this._totalDriveTime;
    }

    get totalDeliveryTime(): number {
        return this._totalDeliveryTime;
    }

    get totalPackages(): number {
        return this._totalPackages;
    }

    get totalStops(): number {
        return this._totalStops;
    }

    get totalDistance(): number {
        return this._totalDistance;
    }

    get totalCost(): number {
        return this._totalCost;
    }
}

export class Assignment {
    private readonly _id: number = 0;
    private readonly _name: string = 'Unset';
    private readonly _color: string = '#00FF00';
    private readonly _startTime: string = '2025-01-01 10:00:00';

    private _packages: number = 0;
    private _stops: number = 0;
    private _cost: number = 0;

    private _totalTime: number = 0;
    private _totalDriveTime: number = 0;
    private _totalDeliveryTime: number = 0;
    private _totalDistance: number = 0;

    private _parts: AssignmentPart[] = [];
    public dataMode: string | null = null;

    constructor(
        id: number,
        name: string,
        color: string,
        startTime: string,
        dataMode: string | null = null
    ) {
        this._id = id;
        this._name = name;
        this._color = color;
        this._startTime = startTime;
        this.dataMode = dataMode;
    }

    public getDay(): string {
        return this._startTime.split(' ')[0];
    }

    public addAssignmentPart(assignment: AssignmentPart) {
        if (assignment.type === AssignmentType.Delivery) {
            this._stops++;
            this._packages += assignment.packages;
            this._totalDeliveryTime += assignment.time;
        } else {
            this._totalDriveTime += assignment.time;
        }

        this._totalTime += assignment.time;
        this._totalDistance += assignment.distance;

        this._parts.push(assignment);
    }

    get id(): number {
        return this._id;
    }

    get name(): string {
        return this._name;
    }

    get color(): string {
        return this._color;
    }

    get startTime(): string {
        return this._startTime;
    }

    get startDay(): string {
        return this._startTime.split(' ')[0];
    }

    get packages(): number {
        return this._packages;
    }

    get stops(): number {
        return this._stops;
    }

    get cost(): number {
        return this._cost;
    }

    set cost(cost: number) {
        this._cost = cost;
    }

    get totalTime(): number {
        return this._totalTime;
    }

    get totalDriveTime(): number {
        return this._totalDriveTime;
    }

    get totalDeliveryTime(): number {
        return this._totalDeliveryTime;
    }

    get totalDistance(): number {
        return this._totalDistance;
    }

    get parts(): AssignmentPart[] {
        return this._parts;
    }
}

export class AssignmentPart {
    private _type: AssignmentType = AssignmentType.Drive;
    private _time: number = 0;
    private _pointName: string = 'Punkt';
    private _fromAddress: string = 'Start';
    private _toAddress: string = 'Koniec';
    private _distance: number = 0;
    private _packages: number = 0;
    private _routeDeliveryIterator: number = 0;

    constructor(
        type: AssignmentType,
        time: number,
        pointName: string = '',
        fromAddress: string = '',
        toAddress: string = '',
        distance: number = 0,
        packages: number = 0,
        routeDeliveryIterator: number = 0
    ) {
        if (this._time < 0) {
            throw new Error('Assignment time cannot be negative');
        }
        if (this._distance < 0) {
            throw new Error('Assignment distance cannot be negative');
        }
        this._type = type;
        this._time = time;
        this._pointName = pointName;
        this._fromAddress = fromAddress;
        this._toAddress = toAddress;
        this._distance = distance;
        this._packages = packages;
        this._routeDeliveryIterator = routeDeliveryIterator;
    }

    get type(): AssignmentType {
        return this._type;
    }

    get time(): number {
        return this._time;
    }

    get pointName(): string {
        return this._pointName;
    }

    get fromAddress(): string {
        return this._fromAddress;
    }

    get toAddress(): string {
        return this._toAddress;
    }

    get distance(): number {
        return this._distance;
    }

    get packages(): number {
        return this._packages;
    }

    get routeDeliveryIterator(): number {
        return this._routeDeliveryIterator;
    }
}
